import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { useARC } from "providers/ARC/ARCProvider";
import { useMap } from "providers/navigation/MapProvider";
import { useLandmarks } from "providers/navigation/LandmarksProvider";
import { useTempMission } from "providers/navigation/TempMissionProvider";
import { useMissionControl } from "providers/navigation/MissionProvider";
import { useControlDebug } from "providers/debug/ControlDebugProvider";
import MapView from "./MapView";
import LandmarkEditDialog from "./LandmarkEditDialog";
import { ConnectionState } from "lib/consts/ARC/connection";
import { point2DToLatLng } from "lib/utils/geometry";
import { getStcmMapListBaseUrl } from "lib/consts/stcmMapList";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { liveGridSnapshotFromMapMetadata } from "lib/navigation/mapEditorGrid";
import { useMapManager } from "providers/navigation/MapManagerProvider";
import { useNavigateToPose } from "providers/navigation/NavigateToPoseProvider";
import { useMissionMapWaypoints } from "lib/mission/useMissionMapWaypoints";
import { useMissionMapPreview } from "lib/mission/useMissionMapPreview";
import {
    useUpdateActionMutation,
    useDeleteActionMutation,
} from "app/services/missionStorageApiEnhanced";
import { useSelector } from "react-redux";

const ARCControlMapView = ({ isMiniView = false } = {}) => {
    const { t } = useTranslation("missions");
    const navigate = useNavigate();
    const location = useLocation();
    const { controlSocket, selectedARC, connectionState } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const {
        loadStcmFromRelativePath,
        saveMap,
        clearMap,
    } = useMapManager();
    const { mapImageUrl, mapMetadata, robotPose, plannedPath } = useMap();
    const { landmarks, addLandmark, updateLandmark, deleteLandmark } = useLandmarks();
    const { waypoints: tempWaypoints, addWaypoint, deleteWaypoint } = useTempMission();
    const missionWaypointInfo = useMissionMapWaypoints();
    const missionMapPreview = useMissionMapPreview();
    const missionWaypoints = missionWaypointInfo.waypoints;
    const showingMissionWaypoints = missionWaypoints.length > 0;
    const displayWaypoints = missionMapPreview
        ? missionWaypoints
        : showingMissionWaypoints
          ? missionWaypoints
          : tempWaypoints;
    // Mission pins are editable only when they belong to the mission open in
    // create/edit (not a live run) and edit chrome is on.
    const missionEditMode = useSelector((state) => state.missionCreation.editMode);
    const missionWaypointsEditable =
        missionWaypointInfo.source === "editing" && missionEditMode;
    const { setShowMissionsInControl } = useMissionControl();
    const { setDebugSection } = useControlDebug();
    const { startNavigateToPoseFromMap } = useNavigateToPose();
    const [updateAction] = useUpdateActionMutation();
    const [deleteAction] = useDeleteActionMutation();

    // Waypoint pin currently armed to be moved ("Move" chosen from its menu);
    // the next map click places it at the new position.
    const [movingWaypointId, setMovingWaypointId] = useState(null);
    const movingWaypoint = movingWaypointId
        ? missionWaypoints.find((waypoint) => waypoint.id === movingWaypointId)
        : null;

    const stcmListBaseUrl = getStcmMapListBaseUrl(selectedARC?.host, isSecure, arcId);

    // Menu state for map clicks and landmark clicks
    const [menuState, setMenuState] = useState({
        visible: false,
        position: null,
        point: null,
        landmark: null,
        waypoint: null,
        type: null // "map", "landmark", "waypoint" (temp), or "missionWaypoint"
    });

    // Dialog state for editing landmarks
    const [editDialog, setEditDialog] = useState({
        visible: false,
        landmark: null
    });

    // Track connection state
    useEffect(() => {
        setDebugSection("Connection State", {
            connectionState,
            hasControlSocket: !!controlSocket,
            hasSelectedARC: !!selectedARC,
            selectedARCSerial: selectedARC?.serialNumber,
            lastUpdate: new Date().toISOString()
        });
    }, [connectionState, controlSocket, selectedARC, setDebugSection]);

    // Debug map metadata
    useEffect(() => {
        if (!mapMetadata) return;

        const widthMeters = mapMetadata.width * mapMetadata.resolution;
        const heightMeters = mapMetadata.height * mapMetadata.resolution;
        const origin = mapMetadata.origin;
        const swLatLng = point2DToLatLng({ x: origin.x, y: origin.y });
        const neLatLng = point2DToLatLng({ x: origin.x + widthMeters, y: origin.y + heightMeters });

        setDebugSection("Map Metadata", {
            "ROS Origin": origin,
            "Width (cells)": mapMetadata.width,
            "Height (cells)": mapMetadata.height,
            "Resolution (m/cell)": mapMetadata.resolution,
            "Width (meters)": widthMeters,
            "Height (meters)": heightMeters,
            "Leaflet SW": swLatLng,
            "Leaflet NE": neLatLng
        });
    }, [mapMetadata, setDebugSection]);

    // Create Robot object for MapView
    const robot = robotPose && selectedARC ? {
        serialNumber: selectedARC.serialNumber,
        pose: robotPose
    } : null;


    // Move a mission waypoint pin: rewrite the NavigateTo action's pose position.
    const applyMissionWaypointMove = useCallback((actionId, point) => {
        const action = missionWaypointInfo.actions.find((a) => a.id === actionId);
        setMovingWaypointId(null);
        if (!action) return;
        const args = { ...(action.args ?? {}) };
        args.pose = {
            ...(args.pose ?? {}),
            position: { x: point.x, y: point.y, z: 0 },
        };
        updateAction({
            id: action.id,
            input: { name: action.name, note: action.note || "", args },
        })
            .catch((err) => console.warn("Failed to move mission waypoint:", err));
    }, [missionWaypointInfo.actions, updateAction]);

    // Handle map click - apply a pending waypoint move or show the map menu
    const handleMapClick = useCallback((point) => {
        if (movingWaypointId) {
            applyMissionWaypointMove(movingWaypointId, point);
            return;
        }
        // Stored-map preview is not the live frame — don't open goal / pose menus.
        if (missionMapPreview) return;
        setMenuState({
            visible: true,
            position: point,
            point: point,
            landmark: null,
            waypoint: null,
            type: "map"
        });
    }, [movingWaypointId, applyMissionWaypointMove, missionMapPreview]);

    // Mission waypoint pin clicked (mission open in create/edit with edit on)
    const handleMissionWaypointClick = useCallback((waypoint) => {
        // Re-clicking the armed pin cancels the pending move.
        if (movingWaypointId === waypoint.id) {
            setMovingWaypointId(null);
            return;
        }
        setMenuState({
            visible: true,
            position: waypoint.pose.position,
            point: waypoint.pose.position,
            landmark: null,
            waypoint,
            type: "missionWaypoint"
        });
    }, [movingWaypointId]);

    const handleMoveMissionWaypoint = useCallback(() => {
        if (!menuState.waypoint) return;
        setMovingWaypointId(menuState.waypoint.id);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.waypoint]);

    const handleDeleteMissionWaypoint = useCallback(() => {
        if (!menuState.waypoint) return;
        deleteAction({ id: menuState.waypoint.id })
            .catch((err) => console.warn("Failed to delete mission waypoint:", err));
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.waypoint, deleteAction]);

    // Disarm pending move when edit chrome goes away or the pins change source.
    useEffect(() => {
        if (movingWaypointId && !missionWaypointsEditable) {
            setMovingWaypointId(null);
        }
    }, [movingWaypointId, missionWaypointsEditable]);

    useEffect(() => {
        if (!missionMapPreview) return;
        setMenuState((prev) => (prev.visible ? { ...prev, visible: false } : prev));
    }, [missionMapPreview]);

    // Handle landmark click - show "Go There" / "Edit" / "Delete" menu
    const handleLandmarkClick = useCallback((landmark) => {
        setMenuState({
            visible: true,
            position: landmark.pose.position,
            point: landmark.pose.position,
            landmark: landmark,
            waypoint: null,
            type: "landmark"
        });
    }, []);

    const handleWaypointClick = useCallback((waypoint) => {
        setMenuState({
            visible: true,
            position: waypoint.pose.position,
            point: waypoint.pose.position,
            landmark: null,
            waypoint,
            type: "waypoint"
        });
    }, []);

    // Nav2 navigate_to_pose (same pose as legacy /goal_pose topic)
    const goToPoint = useCallback(
        (point) => {
            startNavigateToPoseFromMap({
                x: point.x,
                y: point.y,
                ...(Number.isFinite(point.theta) ? { theta: point.theta } : {}),
            });
        },
        [startNavigateToPoseFromMap]
    );

    // Menu action handlers
    const handleGoThere = useCallback(() => {
        goToPoint(menuState.point);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.point, goToPoint]);

    const handleCreateLandmark = useCallback(() => {
        const newLandmark = {
            name: `Landmark ${landmarks.length + 1}`,
            pose: {
                position: menuState.point,
                orientation: { x: 0, y: 0, z: 0, w: 1 }
            }
        };
        addLandmark(newLandmark);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.point, landmarks.length, addLandmark]);

    const handleEditLandmark = useCallback(() => {
        setEditDialog({
            visible: true,
            landmark: menuState.landmark
        });
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.landmark]);

    const handleDeleteLandmark = useCallback(() => {
        deleteLandmark(menuState.landmark.id);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.landmark, deleteLandmark]);

    const handleRemoveWaypoint = useCallback(() => {
        if (!menuState.waypoint) return;
        deleteWaypoint(menuState.waypoint.id);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.waypoint, deleteWaypoint]);

    const handleAddWaypoint = useCallback(() => {
        const goalPose = {
            header: {
                stamp: { sec: 0, nanosec: 0 },
                frame_id: "map"
            },
            pose: {
                position: { x: menuState.point.x, y: menuState.point.y, z: 0 },
                orientation: { x: 0, y: 0, z: 0, w: 1 }
            }
        };
        addWaypoint(goalPose);
        setShowMissionsInControl(true);
        setMenuState(prev => ({ ...prev, visible: false }));
    }, [menuState.point, addWaypoint, setShowMissionsInControl]);

    const handleSaveLandmark = useCallback((updates) => {
        updateLandmark(editDialog.landmark.id, updates);
        setEditDialog({ visible: false, landmark: null });
    }, [editDialog.landmark, updateLandmark]);

    const handleCancel = useCallback(() => {
        setMenuState((prev) => ({ ...prev, visible: false }))
    }, []);

    const onStcmEditStorageMap = useCallback(
        (relativePath, mapManagerRestore) => {
            const baseUrl = getStcmMapListBaseUrl(selectedARC?.host, isSecure, arcId);
            if (!baseUrl) return;
            const relPath = String(relativePath).replace(/\\/g, "/").trim();
            const parentStcmRelPath =
                mapManagerRestore?.focusedStcm ??
                (relPath.toLowerCase().endsWith(".stcm") ? relPath : undefined);
            const liveMapGrid = liveGridSnapshotFromMapMetadata(mapMetadata);
            const state = {
                storageStcmEdit: {
                    baseUrl,
                    relPath,
                    ...(parentStcmRelPath ? { parentStcmRelPath } : {}),
                    ...(liveMapGrid ? { liveMapGrid } : {}),
                },
                ...(mapManagerRestore ? { mapManagerRestore } : {}),
            };
            if (location.pathname.startsWith("/fleet")) {
                navigate("/fleet/map/editor", { state });
            } else {
                navigate("/arc/map/editor", { state });
            }
        },
        [navigate, location.pathname, mapMetadata, selectedARC?.host, isSecure, arcId]
    );

    // Menu options based on context
    const menuOptions =
        menuState.type === "missionWaypoint"
            ? [
                { label: "Move", onClick: handleMoveMissionWaypoint },
                { label: "Delete", onClick: handleDeleteMissionWaypoint },
                { label: "Cancel", onClick: handleCancel },
            ]
            : menuState.type === "waypoint"
            ? [
                { label: "Go There", onClick: handleGoThere },
                { label: "Remove Waypoint", onClick: handleRemoveWaypoint },
                { label: "Cancel", onClick: handleCancel },
            ]
            : menuState.type === "landmark"
              ? [
                    { label: "Go There", onClick: handleGoThere },
                    { label: "Add Waypoint", onClick: handleAddWaypoint },
                    { label: "Edit Landmark", onClick: handleEditLandmark },
                    { label: "Delete Landmark", onClick: handleDeleteLandmark },
                    { label: "Cancel", onClick: handleCancel },
                ]
              : [
                    { label: "Add Waypoint", onClick: handleAddWaypoint },
                    { label: "Create Landmark", onClick: handleCreateLandmark },
                    { label: "Cancel", onClick: handleCancel },
                ];

    const displayMapId = missionMapPreview?.mapMetadata.id ?? mapMetadata?.id;
    const displayMapMetadata = missionMapPreview?.mapMetadata ?? mapMetadata;
    const displayMapImage = missionMapPreview?.imageUrl ?? mapImageUrl;
    const mapLoading =
        (connectionState === ConnectionState.Connected ||
            connectionState === ConnectionState.Connecting) &&
        (!displayMapImage || !displayMapMetadata);

    return (
        <div className="relative h-full w-full">
            <MapView
                key={missionMapPreview ? `preview-${displayMapId}` : `live-${displayMapId ?? "none"}`}
                mapId={displayMapId}
                mapMetadata={displayMapMetadata}
                mapViewData={displayMapImage}
                mapLoading={mapLoading}
                stcmListBaseUrl={stcmListBaseUrl}
                onStcmLoadMap={loadStcmFromRelativePath}
                onStcmSaveMap={saveMap}
                onStcmClearMap={clearMap}
                onStcmEditStorageMap={onStcmEditStorageMap}
                hideStcmToolbar={isMiniView}
                mappingToggleLabel={missionMapPreview ? "PREVIEW" : undefined}
                missionPreviewFilterYaml={missionMapPreview?.filterYaml}
                missionPreviewOverlayGridSource={missionMapPreview?.overlayGridSource}
                robots={missionMapPreview ? [] : robot ? [robot] : []}
                landmarks={missionMapPreview ? [] : landmarks}
                waypoints={displayWaypoints}
                missionActionMarkers={missionWaypointInfo.actionMarkers.map((marker) => ({
                    id: marker.id,
                    actionType: marker.actionType,
                    point: { x: marker.pose.position.x, y: marker.pose.position.y },
                    stackIndex: marker.stackIndex,
                }))}
                plannedPath={missionMapPreview ? [] : plannedPath}
                onClick={handleMapClick}
                landmarkOnClick={missionMapPreview ? undefined : handleLandmarkClick}
                waypointOnClick={
                    missionMapPreview
                        ? missionWaypointsEditable
                            ? handleMissionWaypointClick
                            : undefined
                        : showingMissionWaypoints
                          ? missionWaypointsEditable
                              ? handleMissionWaypointClick
                              : undefined
                          : handleWaypointClick
                }
                selectedPoint={
                    movingWaypoint
                        ? movingWaypoint.pose.position
                        : menuState.visible && menuState.point
                }
                menuVisible={menuState.visible}
                menuPosition={menuState.position}
                menuOptions={menuOptions}
                onNavigateToPoseDrag={missionMapPreview ? undefined : goToPoint}
                onMenuClose={() => {
                    setMenuState(prev => ({ ...prev, visible: false }))
                }
                }
            />
            {missionMapPreview ? (
                <div className="pointer-events-none absolute left-2 top-2 z-[1002] max-w-[16rem] truncate rounded bg-black90/90 px-2 py-1 text-h5 text-black20">
                    {t("subpage.mapPreview", { name: missionMapPreview.label })}
                </div>
            ) : null}

            <LandmarkEditDialog
                visible={editDialog.visible}
                landmark={editDialog.landmark}
                onSave={handleSaveLandmark}
                onClose={() => setEditDialog({ visible: false, landmark: null })}
            />
        </div>
    );
};

export default ARCControlMapView;
